import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, Clock, Package, BookOpen, Calendar, FileText, 
  TrendingUp, AlertCircle, CheckCircle, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const { user, api, isAdmin, isEmployee, isPhysician } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentSupplies, setRecentSupplies] = useState([]);
  const [upcomingSchedules, setUpcomingSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, suppliesRes, schedulesRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/supplies?status=requested'),
          api.get('/schedules', {
            params: {
              start_date: new Date().toISOString().split('T')[0],
              end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
            }
          })
        ]);

        setStats(statsRes.data);
        setRecentSupplies(suppliesRes.data.slice(0, 5));
        
        // Filter schedules for current user if not admin
        let schedules = schedulesRes.data;
        if (!isAdmin) {
          schedules = schedules.filter(s => s.assigned_to === user.id);
        }
        setUpcomingSchedules(schedules.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [api, user, isAdmin]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getPriorityColor = (priority) => {
    return priority === 'urgent' ? 'priority-urgent' : 'priority-normal';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-24 bg-slate-200 rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-2">
          {getGreeting()}, {user?.first_name}!
        </h1>
        <p className="text-teal-100 text-lg">
          {isAdmin && "Here's an overview of today's operations."}
          {isEmployee && "Ready to start your day? Check your schedule and tasks."}
          {isPhysician && "View your upcoming appointments and schedules."}
        </p>
        {!user?.mfa_enabled && (
          <div className="mt-4 p-3 bg-white/10 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-300" />
            <span className="text-sm">
              MFA is not enabled. 
              <Link to="/profile" className="underline ml-1 font-medium">Set up now</Link> for enhanced security.
            </span>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isAdmin && (
          <>
            <Card className="hover:shadow-lg transition-shadow" data-testid="stat-total-users">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Total Users</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow" data-testid="stat-pending-timesheets">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Pending Timesheets</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.pending_timesheets || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
                {stats?.pending_timesheets > 0 && (
                  <Link to="/timesheets" className="text-sm text-teal-600 hover:text-teal-700 mt-2 flex items-center gap-1">
                    Review now <ArrowRight className="h-3 w-3" />
                  </Link>
                )}
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow" data-testid="stat-pending-supplies">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Supply Requests</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.pending_supplies || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Package className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow" data-testid="stat-training-docs">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Training Docs</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.training_docs || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {(isEmployee || isPhysician) && (
          <>
            <Card className="hover:shadow-lg transition-shadow" data-testid="stat-upcoming-shifts">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Upcoming Shifts</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.upcoming_shifts || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <Link to="/schedules" className="text-sm text-teal-600 hover:text-teal-700 mt-2 flex items-center gap-1">
                  View schedule <ArrowRight className="h-3 w-3" />
                </Link>
              </CardContent>
            </Card>

            {isEmployee && (
              <>
                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-my-timesheets">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 font-medium">Pending Timesheets</p>
                        <p className="text-3xl font-bold text-slate-900">{stats?.my_pending_timesheets || 0}</p>
                      </div>
                      <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Clock className="h-6 w-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-training-completed">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 font-medium">Training Completed</p>
                        <p className="text-3xl font-bold text-slate-900">{stats?.my_training_completed || 0}</p>
                      </div>
                      <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Schedule */}
        <Card data-testid="upcoming-schedule-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Upcoming Schedule</CardTitle>
              <CardDescription>Your shifts for the next 7 days</CardDescription>
            </div>
            <Link to="/schedules">
              <Button variant="outline" size="sm" data-testid="view-all-schedules">
                View All
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingSchedules.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No upcoming shifts scheduled</p>
            ) : (
              <div className="space-y-3">
                {upcomingSchedules.map((schedule, index) => (
                  <div 
                    key={schedule.id} 
                    className={`flex items-center justify-between p-3 rounded-lg bg-slate-50 animate-fade-in stagger-${index + 1}`}
                    data-testid={`schedule-item-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{schedule.title}</p>
                        <p className="text-sm text-slate-500">{formatDate(schedule.date)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-900">
                        {schedule.start_time} - {schedule.end_time}
                      </p>
                      {schedule.shift_bucket && (
                        <Badge variant="secondary" className="text-xs">
                          {schedule.shift_bucket}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Supply Requests - Visible to Admin */}
        {isAdmin && (
          <Card data-testid="supply-requests-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Pending Supply Requests</CardTitle>
                <CardDescription>Items waiting for approval</CardDescription>
              </div>
              <Link to="/supplies">
                <Button variant="outline" size="sm" data-testid="view-all-supplies">
                  View All
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentSupplies.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No pending supply requests</p>
              ) : (
                <div className="space-y-3">
                  {recentSupplies.map((supply, index) => (
                    <div 
                      key={supply.id}
                      className={`flex items-center justify-between p-3 rounded-lg bg-slate-50 animate-fade-in stagger-${index + 1}`}
                      data-testid={`supply-item-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Package className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{supply.item_name}</p>
                          <p className="text-sm text-slate-500">Qty: {supply.quantity}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`status-badge ${getPriorityColor(supply.priority)}`}>
                          {supply.priority}
                        </span>
                        <p className="text-xs text-slate-500 mt-1">{supply.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions - Visible to Employees */}
        {isEmployee && (
          <Card data-testid="quick-actions-card">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
              <CardDescription>Common tasks you can perform</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/timesheets" className="block">
                <Button variant="outline" className="w-full justify-start h-14" data-testid="quick-timesheet">
                  <Clock className="h-5 w-5 mr-3 text-amber-600" />
                  <div className="text-left">
                    <p className="font-medium">Submit Timesheet</p>
                    <p className="text-xs text-slate-500">Log your hours for the pay period</p>
                  </div>
                </Button>
              </Link>
              <Link to="/supplies" className="block">
                <Button variant="outline" className="w-full justify-start h-14" data-testid="quick-supply">
                  <Package className="h-5 w-5 mr-3 text-purple-600" />
                  <div className="text-left">
                    <p className="font-medium">Request Supplies</p>
                    <p className="text-xs text-slate-500">Submit a new supply request</p>
                  </div>
                </Button>
              </Link>
              <Link to="/training" className="block">
                <Button variant="outline" className="w-full justify-start h-14" data-testid="quick-training">
                  <BookOpen className="h-5 w-5 mr-3 text-emerald-600" />
                  <div className="text-left">
                    <p className="font-medium">View Training</p>
                    <p className="text-xs text-slate-500">Access training materials</p>
                  </div>
                </Button>
              </Link>
              <Link to="/payroll" className="block">
                <Button variant="outline" className="w-full justify-start h-14" data-testid="quick-payroll">
                  <FileText className="h-5 w-5 mr-3 text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium">Payroll Documents</p>
                    <p className="text-xs text-slate-500">View your paystubs and tax forms</p>
                  </div>
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
